import React, { useState, useCallback, useEffect, useRef } from "react";
import { staffQueue, facilityAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { Btn, LoadingSpinner, selectStyle } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import useQueuePoller from "../../hooks/useQueuePoller";
import ManualIssueModal from "./ManualIssueModal";
import toast from "react-hot-toast";

// Accessibility helpers
const ACCESS_ICONS  = { simple:"📖", hearing:"👂", visual:"👁", mobility:"♿", communication:"💬" };
const ACCESS_LABELS = { simple:"Easy Reading", hearing:"Hearing Impairment", visual:"Visual Impairment", mobility:"Mobility Assistance", communication:"Communication Assistance" };
const ACCESS_COLORS = {
  simple:        { bg:"rgba(96,165,250,0.12)",  color:"#60A5FA",  border:"rgba(96,165,250,0.3)"  },
  hearing:       { bg:"rgba(167,139,250,0.12)", color:"#A78BFA",  border:"rgba(167,139,250,0.3)" },
  visual:        { bg:"rgba(52,211,153,0.12)",  color:"#34D399",  border:"rgba(52,211,153,0.3)"  },
  mobility:      { bg:"rgba(251,191,36,0.12)",  color:"#FBBF24",  border:"rgba(251,191,36,0.3)"  },
  communication: { bg:"rgba(244,114,182,0.12)", color:"#F472B6",  border:"rgba(244,114,182,0.3)" },
};
const ACCESS_NOTES = {
  mobility:      "Do not call patient to counter — send staff to patient.",
  communication: "Patient has communication difficulties — prepare communication support card.",
};

// Full language set a patient can pick at the kiosk (see
// apps/queue/views.py SUPPORTED_LANGUAGES). Only fr/ar/zh have a live
// LibreTranslate model (see translation.py) — the rest (tw/ga/ee/ha) only
// ever get translated if a facility admin has hand-written that language's
// canned-message text; free-text staff replies to those patients are sent
// in English as-is. LANGUAGE_ENGINE reflects that real backend capability,
// not just which codes are selectable, so the badge tooltip never overstates
// what actually happens to a message.
const LANGUAGE_NAMES  = { tw: "Twi", ga: "Ga", ee: "Ewe", ha: "Hausa", fr: "French", ar: "Arabic", zh: "Chinese" };
const LANGUAGE_ENGINE = { fr: "live", ar: "live", zh: "live" }; // everything else: canned-only

function languageBadgeInfo(code) {
  if (!code || code === "en") return null;
  const name = LANGUAGE_NAMES[code] || code.toUpperCase();
  const engine = LANGUAGE_ENGINE[code];
  const tooltip = engine === "live"
    ? `Speaks ${name} — free-text messages are auto-translated for this patient.`
    : `Speaks ${name} — no live translation engine for this language; only pre-written canned messages in ${name} will show correctly. Free-text is sent in English.`;
  return { name, tooltip, live: engine === "live" };
}

// Small "language barrier" flag shown next to a patient's name/ticket so
// staff notice at a glance, before opening the conversation panel, that a
// language other than English is involved (see conversation panel's own
// per-message translation note for the fuller picture).
function LanguageBadge({ code, size = "normal" }) {
  const info = languageBadgeInfo(code);
  if (!info) return null;
  const small = size === "small";
  return (
    <span
      title={info.tooltip}
      style={{
        display: "inline-flex", alignItems: "center", gap: "4px",
        fontSize: small ? "9px" : "12px", fontWeight: "700",
        letterSpacing: "0.04em",
        borderRadius: small ? "5px" : "8px",
        padding: small ? "1px 5px" : "4px 10px",
        background: info.live ? "rgba(96,165,250,0.12)" : "rgba(245,158,11,0.12)",
        color: info.live ? "#60A5FA" : "#F59E0B",
        border: `1px solid ${info.live ? "rgba(96,165,250,0.3)" : "rgba(245,158,11,0.3)"}`,
        cursor: "help",
      }}
    >
      🌐 {info.name}{!info.live && !small ? " ⚠" : ""}
    </span>
  );
}

function UnreadDot() {
  return (
    <span style={{
      position: "absolute", top: "-3px", right: "-3px",
      width: "9px", height: "9px", borderRadius: "50%",
      background: "#EF4444", border: "2px solid var(--page-bg)",
    }} />
  );
}

function AccessibilityBadge({ mode }) {
  const c = ACCESS_COLORS[mode]; const note = ACCESS_NOTES[mode];
  if (!c) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      <div style={{ display:"inline-flex", alignItems:"center", gap:"6px", borderRadius:"8px", padding:"5px 12px", fontSize:"12px", fontWeight:"700", letterSpacing:"0.06em", background:c.bg, color:c.color, border:`1px solid ${c.border}` }}>
        {ACCESS_ICONS[mode]} {ACCESS_LABELS[mode].toUpperCase()}
      </div>
      {note && <div style={{ fontSize:"12px", color:c.color, fontStyle:"italic", padding:"6px 10px", borderRadius:"6px", background:c.bg, border:`1px solid ${c.border}`, lineHeight:1.4 }}>⚠ {note}</div>}
    </div>
  );
}

export default function StaffQueuePage() {
  const { user }                           = useAuth();
  const { queue, loading, error, refresh } = useQueuePoller();

  const [calling,       setCalling]       = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [manualModal,   setManualModal]   = useState(false);
  const [reannouncing,  setReannouncing]  = useState(false);
  const [isAway,        setIsAway]        = useState(user?.is_away || false);
  const [awaySince,     setAwaySince]     = useState(user?.away_since || null);
  const [awayTimer,     setAwayTimer]     = useState(null);
  const [counterModal,  setCounterModal]  = useState(false);
  const [selectedCounter, setSelectedCounter] = useState(null);
  const counterResolvedForStation = useRef(null);
  const [messageTicket, setMessageTicket] = useState(null);

  // Quick alternative to the facility-admin queue override: rather than
  // waiting for an admin, the staff member who sent a ticket somewhere can
  // pull it back themselves while it's still waiting (uncalled) at its
  // destination. Facility toggles between two behaviors via recall_mode:
  // "simple" always reverses back to where it came from; "redirect" lets
  // the sender pick any station, same as the admin override's move action.
  const [myTransfers,  setMyTransfers]  = useState([]);
  const [recallMode,   setRecallMode]   = useState("simple");
  const [stations,     setStations]     = useState([]);
  const [recallPicker, setRecallPicker] = useState(null); // ticket, when redirect-mode picker is open
  const [recalling,    setRecalling]    = useState(false);

  const refreshMyTransfers = useCallback(() => {
    staffQueue.myTransfers().then(res => setMyTransfers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    facilityAdmin.getSettings().then(res => setRecallMode(res.data?.recall_mode || "simple")).catch(() => {});
    refreshMyTransfers();
    const interval = setInterval(refreshMyTransfers, 15000);
    return () => clearInterval(interval);
  }, [refreshMyTransfers]);

  const handleRecall = useCallback(async (ticketId, stationId = null) => {
    setRecalling(true);
    try {
      const { data } = await staffQueue.recallTicket(ticketId, stationId);
      toast.success(`${data.ticket_number} recalled to ${data.current_station_name}.`);
      setRecallPicker(null);
      refreshMyTransfers();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to recall ticket.");
    } finally { setRecalling(false); }
  }, [refresh, refreshMyTransfers]);

  const openRecall = useCallback((ticket) => {
    if (recallMode === "redirect") {
      if (stations.length === 0) {
        facilityAdmin.listStations().then(res => setStations(res.data?.results || res.data || [])).catch(() => {});
      }
      setRecallPicker(ticket);
    } else {
      handleRecall(ticket.id);
    }
  }, [recallMode, stations.length, handleRecall]);

  // Resolve which counter this staff member is operating, once per
  // station: restore a counter chosen in an earlier session (now returned
  // by the backend as queue.selected_counter — previously the frontend
  // only ever looked at user?.counter, which the login payload never
  // actually populated, so this modal re-prompted on every reload even
  // after a counter had already been picked). If the station only has one
  // active counter there's no real choice to make, so pick it silently
  // instead of prompting. Only fall through to the modal when there's an
  // actual unresolved choice — 2+ counters and nothing selected yet.
  useEffect(() => {
    if (loading || !queue?.station) return;
    const stationId = queue.station.id;
    if (counterResolvedForStation.current === stationId) return;
    counterResolvedForStation.current = stationId;

    const counters = queue.station.counters || [];
    if (queue.selected_counter) {
      setSelectedCounter(queue.selected_counter);
    } else if (counters.length === 1) {
      const only = counters[0];
      staffQueue.selectCounter(only.id)
        .then(() => setSelectedCounter(only))
        .catch(() => setCounterModal(true));
    } else if (counters.length > 1) {
      setCounterModal(true);
    }
  }, [loading, queue?.station, queue?.selected_counter]);

  // Prompt staff the moment a waiting ticket crosses its station's Max
  // Wait Time threshold — is_overdue itself (from the serializer) already
  // drives a persistent badge in the waiting list, but that's silent if
  // nobody happens to be looking at the list right then. Tracking which
  // ticket ids were already overdue on the previous poll lets this fire a
  // toast exactly once, right when a ticket first tips over, rather than
  // re-alerting on every poll for as long as it stays overdue.
  const overdueSeenRef = useRef(new Set());
  useEffect(() => {
    const waiting = queue?.waiting || [];
    const stillOverdue = new Set();
    waiting.forEach(t => {
      if (!t.is_overdue) return;
      stillOverdue.add(t.id);
      if (!overdueSeenRef.current.has(t.id)) {
        toast(`${t.ticket_number} — ${t.patient_name} has exceeded the wait time threshold.`, {
          icon: "⏰", duration: 6000,
        });
      }
    });
    overdueSeenRef.current = stillOverdue;
  }, [queue?.waiting]);

  // Away timer
  useEffect(() => {
    if (!isAway || !awaySince) { setAwayTimer(null); return; }
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(awaySince).getTime()) / 60000);
      setAwayTimer(diff);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [isAway, awaySince]);

  const handleCallNext = useCallback(async () => {
    if (isAway) return;
    setCalling(true);
    try {
      const { data } = await staffQueue.callNext({ counter_id: selectedCounter?.id || null });
      if (data?.ticket_number) toast.success(`Called: ${data.ticket_number} — ${data.patient_name}`);
      else toast("Queue is empty.", { icon:"ℹ️" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.error || "Failed to call next."); }
    finally     { setCalling(false); }
  }, [refresh, isAway, selectedCounter]);

  const handleCallSpecific = useCallback(async (ticketId) => {
    if (isAway) return;
    setCalling(true);
    try {
      const { data } = await staffQueue.callSpecific({
        ticket_id: ticketId,
        counter_id: selectedCounter?.id || null,
      });
      toast.success(`Called: ${data.ticket_number} — ${data.patient_name}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to call patient.");
    } finally { setCalling(false); }
  }, [refresh, isAway, selectedCounter]);

  const handleReannounce = useCallback(async () => {
    if (!queue?.serving) return;
    setReannouncing(true);
    try { await staffQueue.reAnnounce(queue.serving.id); toast.success("Re-announced."); }
    catch { toast.error("Failed to re-announce."); }
    finally { setReannouncing(false); }
  }, [queue]);

  const [skipping, setSkipping] = useState(false);
  const [holding,  setHolding]  = useState(false);

  const handleSkip = useCallback(async () => {
    if (!queue?.serving) return;
    setSkipping(true);
    try {
      await staffQueue.skip(queue.serving.id);
      toast(`${queue.serving.ticket_number} skipped — back in the queue.`, { icon: "⏭" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to skip.");
    } finally { setSkipping(false); }
  }, [queue, refresh]);

  const handleHold = useCallback(async () => {
    if (!queue?.serving) return;
    setHolding(true);
    try {
      await staffQueue.hold(queue.serving.id);
      toast(`${queue.serving.ticket_number} on hold — won't be auto-called until you call them again.`, { icon: "⏸" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to place on hold.");
    } finally { setHolding(false); }
  }, [queue, refresh]);

  const handleEscalate = useCallback(async (ticketId, ticketNumber, priority = "emergency") => {
    try {
      await staffQueue.escalatePriority(ticketId, priority);
      toast.success(priority === "normal"
        ? `${ticketNumber || "Ticket"} emergency priority removed.`
        : `${ticketNumber || "Ticket"} marked as emergency.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update priority.");
    }
  }, [refresh]);

  const handleGoAway = async () => {
    try {
      const { data } = await staffQueue.goAway();
      setIsAway(true); setAwaySince(data.away_since);
      toast("You're on break. Come back when ready.", { icon:"☕" });
    } catch { toast.error("Failed to update status."); }
  };

  const handleImBack = async () => {
    try {
      await staffQueue.imBack();
      setIsAway(false); setAwaySince(null);
      toast.success("Welcome back!");
    } catch { toast.error("Failed to update status."); }
  };

  const handleSelectCounter = async (counter) => {
    try {
      await staffQueue.selectCounter(counter?.id || null);
      setSelectedCounter(counter);
      setCounterModal(false);
      toast.success(counter ? `Now operating ${counter.name}` : "Counter cleared.");
    } catch { toast.error("Failed to select counter."); }
  };

  if (!loading && (error === "No station assigned." || (!queue?.station && !loading))) {
    return (
      <div style={styles.root}>
        <div style={styles.noStation}>
          <span style={{ fontSize:"48px" }}>🚫</span>
          <h2 style={{ color:"var(--text-primary)", margin:"16px 0 8px", fontSize:"20px" }}>No station assigned</h2>
          <p style={{ color:"var(--text-secondary)", fontSize:"14px", maxWidth:"300px", textAlign:"center", lineHeight:"1.6" }}>
            You haven't been assigned to a station yet. Ask your Facility Admin to assign you one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* ── Header ── */}
      <div style={{ ...styles.header, borderBottom: isAway ? "2px solid rgba(234,179,8,0.4)" : "none" }}>
        <div>
          <h1 style={styles.title}>{queue?.station?.name || "My Queue"}</h1>
          <p style={styles.sub}>
            {user?.name}
            {queue?.station?.code && <span style={styles.stationCode}>{queue.station.code}</span>}
            {selectedCounter && <span style={{ ...styles.stationCode, background:"rgba(52,211,153,0.12)", color:"#34D399" }}>{selectedCounter.name}</span>}
          </p>
        </div>
        <div style={styles.headerActions}>
          {/* Away / Back button */}
          {isAway ? (
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <span style={{ fontSize:"13px", color:"#EAB308", background:"rgba(234,179,8,0.1)", border:"1px solid rgba(234,179,8,0.25)", borderRadius:"8px", padding:"6px 12px" }}>
                ☕ On break{awayTimer !== null ? ` — ${awayTimer} min` : ""}
              </span>
              <Btn variant="ghost" size="sm" onClick={handleImBack}>✅ I'm Back</Btn>
            </div>
          ) : (
            <Btn variant="ghost" size="sm" onClick={handleGoAway}>☕ Take a Break</Btn>
          )}
          {selectedCounter && (
            <Btn variant="ghost" size="sm" onClick={() => setCounterModal(true)}>
              Change Counter
            </Btn>
          )}
          <Btn variant="ghost" size="sm" onClick={() => setManualModal(true)}>+ Manual Ticket</Btn>
          <div style={styles.waitingBadge}>
            <span style={styles.waitingNum}>{queue?.waiting_count ?? 0}</span>
            <span style={styles.waitingLabel}>waiting</span>
          </div>
        </div>
      </div>

      {loading && !queue ? <LoadingSpinner message="Loading queue…" /> : (
        <div style={styles.layout}>
          <div style={styles.leftCol}>
            <ServingPanel
              serving={queue?.serving}
              outcomes={queue?.outcomes || []}
              calling={calling}
              reannouncing={reannouncing}
              onCallNext={handleCallNext}
              onReannounce={handleReannounce}
              onSkip={handleSkip}
              skipping={skipping}
              onHold={handleHold}
              holding={holding}
              onTransfer={() => setTransferModal(true)}
              onEscalate={handleEscalate}
              onRefresh={refresh}
              waitingCount={queue?.waiting_count || 0}
              isAway={isAway}
            />
          </div>
          <div style={styles.rightCol}>
            {queue?.serving && (
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "10px" }}>
                Calling is paused while you are serving {queue.serving.ticket_number}: transfer, skip or hold it first.
              </div>
            )}
            <WaitingList
              tickets={queue?.waiting || []}
              returning={queue?.returning || []}
              onCallSpecific={handleCallSpecific}
              onEscalate={handleEscalate}
              onMessage={setMessageTicket}
              onLinked={refresh}
              calling={calling || !!queue?.serving}
              isAway={isAway}
            />
            {queue?.todays_appointments?.length > 0 && (
              <AppointmentQueuePanel
                appointments={queue.todays_appointments}
                onCall={handleCallSpecific}
                calling={calling || !!queue?.serving}
                isAway={isAway}
              />
            )}
            {queue?.returning?.length > 0 && (
              <ReturningPanel
                returning={queue.returning}
                onCall={handleCallSpecific}
                calling={calling || !!queue?.serving}
                isAway={isAway}
              />
            )}
            {myTransfers.length > 0 && (
              <MyTransfersPanel
                transfers={myTransfers}
                onRecall={openRecall}
                recalling={recalling}
              />
            )}
          </div>
        </div>
      )}

      <TransferModal open={transferModal} onClose={() => setTransferModal(false)} serving={queue?.serving} outcomes={queue?.outcomes || []} onDone={() => { setTransferModal(false); refresh(); }} />
      <ManualIssueModal open={manualModal} onClose={() => setManualModal(false)} onDone={() => { setManualModal(false); refresh(); }} />
      <CounterSelectModal open={counterModal} onClose={() => setCounterModal(false)} station={queue?.station} onSelect={handleSelectCounter} current={selectedCounter} />
      <ConversationModal ticket={messageTicket} onClose={() => setMessageTicket(null)} onRead={refresh} />
      <RecallPickerModal
        ticket={recallPicker}
        stations={stations}
        onClose={() => setRecallPicker(null)}
        onConfirm={(stationId) => handleRecall(recallPicker.id, stationId)}
        recalling={recalling}
      />
    </div>
  );
}

// ── Conversation ──────────────────────────────────────────────────────────────
// Two-way thread for a ticket: shows canned messages staff already sent
// plus the patient's own free-text replies (typed on their tracking page,
// stored and shown as-is — there is no live translation of patient-typed
// text, see PatientReply's model docstring), then lets staff pick another
// canned FAQ message (by its English text — see docs/PATIENT_LOCALIZATION.md)
// to send. Mounting this body marks any unread replies as read server-side.
// Used two ways: docked inline under the "Now Serving" panel (so the whole
// exchange reads like a chat, per the "so it can be easily appreciated"
// request) for the currently-serving ticket, and inside a Modal for
// waiting-list rows where there's no dedicated panel to dock into.
const CONVERSATION_POLL_MS = 6000;

function ConversationBody({ ticket, onRead }) {
  const { user } = useAuth();
  const [thread,   setThread]   = useState([]);
  const [canned,   setCanned]   = useState([]);
  const [freeText, setFreeText] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [sending,  setSending]  = useState(false);

  // Silent refresh — used by both the initial load's polling handoff and
  // the interval below. Doesn't touch the loading spinner or re-fetch
  // canned messages (those don't change while the panel is open), and
  // marks any new patient replies as read same as the initial load.
  const refreshThread = useCallback(() => {
    if (!ticket) return;
    staffQueue.conversation(ticket.id)
      .then(res => { setThread(res.data?.thread || []); onRead && onRead(); })
      .catch(() => {});
  }, [ticket?.id]);

  useEffect(() => {
    if (!ticket) return;
    setLoading(true);
    Promise.all([
      staffQueue.conversation(ticket.id),
      facilityAdmin.listCannedMessages(),
    ])
      .then(([convoRes, cannedRes]) => {
        setThread(convoRes.data?.thread || []);
        setCanned((cannedRes.data?.results || cannedRes.data).filter(m => m.is_active));
      })
      .catch(() => toast.error("Failed to load conversation."))
      .finally(() => { setLoading(false); onRead && onRead(); });
  }, [ticket?.id]);

  // Patient replies don't come from an action staff take — poll while the
  // conversation is open so an incoming reply shows up without staff
  // having to close and reopen the panel.
  useEffect(() => {
    if (!ticket) return;
    const interval = setInterval(refreshThread, CONVERSATION_POLL_MS);
    return () => clearInterval(interval);
  }, [ticket?.id, refreshThread]);

  const send = async (cannedMessage) => {
    if (!ticket) return;
    setSending(true);
    try {
      const { data } = await staffQueue.sendMessage(ticket.id, { canned_message_id: cannedMessage.id });
      setThread(t => [...t, { id: data.id, text: data.text_en, from: "staff", sent_by_name: user?.name, sent_at: data.sent_at }]);
      toast.success(`Message sent to ${ticket.ticket_number}.`);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to send message.");
    } finally { setSending(false); }
  };

  const sendFreeText = async () => {
    const trimmed = freeText.trim();
    if (!ticket || !trimmed || sending) return;
    setSending(true);
    try {
      const { data } = await staffQueue.sendMessage(ticket.id, { text: trimmed });
      setThread(t => [...t, { id: data.id, text: data.text_en, from: "staff", sent_by_name: user?.name, sent_at: data.sent_at }]);
      setFreeText("");
      toast.success(`Message sent to ${ticket.ticket_number}.`);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to send message.");
    } finally { setSending(false); }
  };

  if (loading) return <LoadingSpinner message="Loading conversation…" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {thread.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "220px", overflowY: "auto" }}>
          {thread.map((m, i) => (
            <div key={m.id || i} style={{
              alignSelf: m.from === "patient" ? "flex-start" : "flex-end",
              maxWidth: "80%", padding: "8px 12px", borderRadius: "10px",
              fontSize: "13px", lineHeight: 1.4,
              background: m.from === "patient" ? "rgba(52,211,153,0.12)" : "rgba(37,99,235,0.15)",
              color: "var(--text-primary)",
              border: `1px solid ${m.from === "patient" ? "rgba(52,211,153,0.3)" : "rgba(37,99,235,0.3)"}`,
            }}>
              <div style={{ fontSize: "10px", fontWeight: "700", opacity: 0.7, marginBottom: "2px", textTransform: "uppercase" }}>
                {m.from === "patient" ? "Patient" : (m.sent_by_name || "Staff")}
                {m.translated && " · translated"}
              </div>
              {m.text}
              {m.original_text && (
                <div style={{ fontSize: "11px", opacity: 0.6, marginTop: "4px", fontStyle: "italic" }}>
                  "{m.original_text}"
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>
          Send a message
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
          <input
            style={{
              flex: 1, background: "var(--input-bg)", border: "1px solid var(--surface-border)",
              borderRadius: "10px", padding: "10px 12px", color: "var(--text-primary)",
              fontSize: "13px", fontFamily: "inherit", outline: "none",
            }}
            value={freeText}
            onChange={e => setFreeText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") sendFreeText(); }}
            placeholder="Type a message…"
            maxLength={300}
            disabled={sending}
          />
          <Btn onClick={sendFreeText} disabled={sending || !freeText.trim()}>Send</Btn>
        </div>
        <div style={{ fontSize: "11px", color: "var(--text-muted)", marginBottom: "12px" }}>
          {languageBadgeInfo(ticket?.language)?.tooltip
            || "Sent in English — this patient's ticket language is English."}
        </div>

        {canned.length === 0 ? (
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
            No canned quick-replies configured yet. Ask your Facility Admin to add some under
            <strong> FAQ / Messages</strong> in the admin panel — they'll show up here as one-click options.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "30vh", overflowY: "auto" }}>
            {canned.map(m => (
              <button
                key={m.id}
                onClick={() => send(m)}
                disabled={sending}
                style={{
                  textAlign: "left", padding: "12px 14px", borderRadius: "10px",
                  border: "1px solid var(--surface-border)", background: "var(--input-bg)",
                  color: "var(--text-primary)", fontSize: "13px", fontWeight: "500",
                  cursor: sending ? "not-allowed" : "pointer", fontFamily: "inherit",
                  opacity: sending ? 0.6 : 1,
                }}
              >
                {m.text_en}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationModal({ ticket, onClose, onRead }) {
  return (
    <Modal open={!!ticket} onClose={onClose} title={`Conversation — ${ticket?.ticket_number || ""}`} width="480px">
      <ConversationBody ticket={ticket} onRead={onRead} />
    </Modal>
  );
}

// ── Counter Select Modal ───────────────────────────────────────────────────────
function CounterSelectModal({ open, onClose, station, onSelect, current }) {
  const counters = station?.counters?.filter(c => c.is_active !== false) || [];
  return (
    <Modal open={open} onClose={onClose} title={`Select Your Counter — ${station?.name || ""}`} width="420px">
      <p style={{ fontSize:"13px", color:"var(--text-secondary)", margin:"0 0 16px" }}>
        Which counter are you operating today?
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
        {counters.length === 0 ? (
          <div style={{ color:"var(--text-secondary)", fontSize:"13px" }}>No counters configured for this station.</div>
        ) : counters.map(c => (
          <button key={c.id} onClick={() => onSelect(c)} style={{
            padding:"12px 16px", borderRadius:"10px", cursor:"pointer",
            border:`2px solid ${current?.id === c.id ? "#2563EB" : "var(--input-border)"}`,
            background: current?.id === c.id ? "var(--surface-border)" : "var(--input-bg)",
            color:"var(--text-primary)", fontFamily:"inherit", textAlign:"left",
            display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <span style={{ fontSize:"14px", fontWeight:"600" }}>{c.name}</span>
            <span style={{ fontSize:"11px", color:"var(--text-secondary)" }}>Counter {c.number}</span>
          </button>
        ))}
      </div>
      <div style={{ marginTop:"12px", paddingTop:"12px", borderTop:"1px solid var(--surface-border)" }}>
        <Btn variant="ghost" onClick={() => onSelect(null)} style={{ width:"100%", justifyContent:"center" }}>
          Continue without counter selection
        </Btn>
      </div>
    </Modal>
  );
}

// ── Serving Panel ─────────────────────────────────────────────────────────────
function ServingPanel({ serving, outcomes, calling, reannouncing, onCallNext, onReannounce, onSkip, skipping, onHold, holding, onTransfer, onEscalate, onRefresh, waitingCount, isAway }) {
  const [convoOpen, setConvoOpen] = useState(false);

  // Don't let the conversation panel stay open across a different ticket
  // being called next — a fresh serving ticket should start collapsed.
  useEffect(() => { setConvoOpen(false); }, [serving?.id]);

  return (
    <div style={SP.root}>
      <div style={SP.label}>NOW SERVING</div>
      {serving ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={SP.ticketNum}>{serving.ticket_number}</div>
            {serving.is_appointment && (
              <span style={{
                fontSize: "12px", fontWeight: "700", color: "#A78BFA", background: "rgba(167,139,250,0.12)",
                border: "1px solid rgba(167,139,250,0.3)", borderRadius: "8px", padding: "4px 10px", marginBottom: "8px",
              }}>
                📅 Appointment
              </span>
            )}
          </div>
          {serving.priority !== "normal" ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ ...SP.priorityBadge, background: serving.priority === "emergency" ? "rgba(239,68,68,0.15)" : "rgba(234,179,8,0.15)", color: serving.priority === "emergency" ? "#EF4444" : "#EAB308", border:`1px solid ${serving.priority === "emergency" ? "#EF444433" : "#EAB30833"}` }}>
                {serving.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
              </div>
              <button
                onClick={() => onEscalate && onEscalate(serving.id, serving.ticket_number, "normal")}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "12px", color: "var(--text-muted)", textDecoration: "underline",
                  fontFamily: "inherit", padding: 0,
                }}
              >
                Unmark
              </button>
            </div>
          ) : (
            <button
              onClick={() => onEscalate && onEscalate(serving.id, serving.ticket_number, "emergency")}
              style={{
                alignSelf: "flex-start", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: "8px", color: "#EF4444", fontSize: "12px", fontWeight: "700",
                padding: "5px 12px", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              🚨 Mark as Emergency
            </button>
          )}
          {serving.accessibility_mode && serving.accessibility_mode !== "none" && (
            <AccessibilityBadge mode={serving.accessibility_mode} />
          )}
          <div style={SP.patientCard}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <div style={SP.patientName}>{serving.patient_name}</div>
              <LanguageBadge code={serving.language} />
            </div>
            {serving.group_members?.length > 0 && (
              <div style={{ margin: "8px 0", padding: "8px 10px", borderRadius: "8px", background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.25)" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "#60A5FA", marginBottom: "4px" }}>
                  👨‍👩‍👧 With {serving.patient_name.split(" ")[0]} ({serving.group_members.length}) — handled together
                </div>
                {serving.group_members.map(m => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "var(--text-primary)", padding: "2px 0" }}>
                    <span>{m.ticket_number} · {m.patient_name}</span>
                    <button
                      title="Handle this person separately"
                      onClick={async () => {
                        try { await staffQueue.ungroup(m.id); toast.success(`${m.patient_name} split off.`); onRefresh && onRefresh(); }
                        catch (e) { toast.error(e.response?.data?.error || "Could not split."); }
                      }}
                      style={{ background: "transparent", border: "1px solid var(--surface-border)", borderRadius: "6px", color: "var(--text-secondary)", fontSize: "11px", padding: "2px 8px", cursor: "pointer", fontFamily: "inherit" }}
                    >
                      Split off
                    </button>
                  </div>
                ))}
              </div>
            )}
            {serving.patient_phone  && <div style={SP.patientDetail}>📱 {serving.patient_phone}</div>}
            {serving.insurance_type && <div style={SP.patientDetail}>🏥 {serving.insurance_type}</div>}
            {serving.reason_for_visit && <div style={SP.patientDetail}>📋 {serving.reason_for_visit}</div>}
            <div style={SP.patientDetail}>⏱ Waiting {Math.round(serving.total_wait_minutes ?? 0)} min</div>
          </div>
          {serving.journey?.length > 0 && (
            <div style={SP.journey}>
              {serving.journey.map((j, i) => (
                <div key={j.id || i} style={SP.journeyStep}>
                  <div style={{ ...SP.journeyDot, background: j.status === "completed" ? "#22C55E" : "#2563EB" }} />
                  <span style={SP.journeyStation}>{j.station_name}</span>
                  {j.outcome_label && <span style={SP.journeyOutcome}>→ {j.outcome_label}</span>}
                </div>
              ))}
            </div>
          )}
          <div style={SP.actions}>
            <Btn variant="primary" onClick={onTransfer} style={{ flex:1, padding:"12px" }}>Complete / Transfer →</Btn>
            <Btn variant="ghost" onClick={onReannounce} disabled={reannouncing} style={{ padding:"12px 16px" }} title="Re-announce on display board">{reannouncing ? "…" : "📣"}</Btn>
            <div style={{ position: "relative" }}>
              <Btn variant={convoOpen ? "primary" : "ghost"} onClick={() => setConvoOpen(o => !o)} style={{ padding:"12px 16px" }} title={languageBadgeInfo(serving.language)?.tooltip || "Conversation"}>💬</Btn>
              {!convoOpen && serving.unread_reply_count > 0 && <UnreadDot />}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Btn
              variant="ghost" onClick={onSkip} disabled={skipping}
              style={{ flex: 1, padding: "10px", color: "#EF4444", borderColor: "rgba(239,68,68,0.3)" }}
              title="Called but patient didn't respond — returns to the queue, tagged as skipped"
            >
              {skipping ? "…" : "⏭ Skip"}
            </Btn>
            <Btn
              variant="ghost" onClick={onHold} disabled={holding}
              style={{ flex: 1, padding: "10px", color: "#F59E0B", borderColor: "rgba(245,158,11,0.3)" }}
              title="Not ready yet (e.g. waiting on lab results) — won't be auto-called until you call them again"
            >
              {holding ? "…" : "⏸ Hold"}
            </Btn>
          </div>
          {convoOpen && (
            <div style={SP.inlineConvo}>
              <ConversationBody ticket={serving} onRead={onRefresh} />
            </div>
          )}
        </>
      ) : (
        <div style={SP.idle}>
          {isAway ? (
            <>
              <div style={SP.idleIcon}>☕</div>
              <div style={{ ...SP.idleText, color:"#EAB308" }}>On Break</div>
              <div style={SP.idleSub}>Return from break to call patients</div>
            </>
          ) : (
            <>
              <div style={SP.idleIcon}>🟢</div>
              <div style={SP.idleText}>Ready</div>
              <div style={SP.idleSub}>{waitingCount > 0 ? `${waitingCount} patient${waitingCount !== 1 ? "s" : ""} waiting` : "Queue is empty"}</div>
              <Btn onClick={onCallNext} disabled={calling || waitingCount === 0 || isAway} style={{ marginTop:"20px", padding:"14px 32px", fontSize:"16px", width:"100%" }}>
                {calling ? "Calling…" : waitingCount > 0 ? "Call Next ▶" : "No one waiting"}
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Waiting List ──────────────────────────────────────────────────────────────
// Origin colors are assigned deterministically (hash of the station name)
// so the same upstream station always gets the same color on a given
// staff member's screen without any per-facility configuration — this
// works at any station fed by any number of upstream stations, not just
// a hardcoded pair.
const ORIGIN_PALETTE = [
  { color: "#60A5FA", bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.25)"  },
  { color: "#34D399", bg: "rgba(52,211,153,0.1)",  border: "rgba(52,211,153,0.25)"  },
  { color: "#FBBF24", bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.25)"  },
  { color: "#F472B6", bg: "rgba(244,114,182,0.1)", border: "rgba(244,114,182,0.25)" },
  { color: "#A78BFA", bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.25)" },
];
function originStyle(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return ORIGIN_PALETTE[hash % ORIGIN_PALETTE.length];
}

function groupByOrigin(tickets) {
  const groups = new Map();
  for (const t of tickets) {
    const key = t.incoming_from_station || "New";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }
  return groups;
}

function WaitingList({ tickets, returning, onCallSpecific, onEscalate, onMessage, calling, isAway, onLinked }) {
  const [picked, setPicked] = useState([]);
  const [linking, setLinking] = useState(false);
  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const linkPicked = async () => {
    setLinking(true);
    try {
      await staffQueue.linkGroup(picked);
      toast.success("Linked as a group — they will be called together.");
      setPicked([]);
      onLinked && onLinked();
    } catch (e) { toast.error(e.response?.data?.error || "Could not link."); }
    finally { setLinking(false); }
  };
  // Tickets already surfaced in the Returning Patients panel (below) are
  // excluded here so the same ticket doesn't show twice — that panel is
  // specifically for patients who've been to THIS station before today
  // (e.g. back a second time once lab results are ready) and already has
  // its own priority "Call" action, so repeating them up here under a
  // "From X" tag was pure duplication, not a second useful signal.
  const returningIds = new Set((returning || []).map(r => r.id));
  const visibleTickets = tickets.filter(t => !returningIds.has(t.id));

  const groups = groupByOrigin(visibleTickets);
  // Show grouping whenever at least one ticket has a known origin other
  // than a fresh kiosk arrival — e.g. a single patient transferred in from
  // Lab still needs to be visibly called out as "From Laboratory" so staff
  // don't mistake them for ready-to-call, even if nobody else happens to
  // be waiting right now. A station fed by only one source (or entirely
  // fresh arrivals) still renders as a plain flat list.
  const showGroups = [...groups.keys()].some(k => k !== "New");

  let runningIndex = 0;

  return (
    <div style={WL.root}>
      <div style={WL.header}>
        <span style={WL.title}>Waiting</span>
        <span style={WL.count}>{visibleTickets.length}</span>
      </div>
      {picked.length >= 2 && (
        <div style={{ padding: "8px 12px" }}>
          <Btn onClick={linkPicked} disabled={linking || isAway}>
            {linking ? "Linking…" : `👨‍👩‍👧 Link ${picked.length} as a group`}
          </Btn>
        </div>
      )}
      {visibleTickets.length === 0 ? <div style={WL.empty}>No one waiting.</div> : (
        <div style={WL.list}>
          {[...groups.entries()].map(([origin, group]) => (
            <div key={origin}>
              {showGroups && (
                <div style={{ ...WL.groupHeader, color: originStyle(origin).color, background: originStyle(origin).bg, borderBottom: `1px solid ${originStyle(origin).border}` }}>
                  {origin === "New" ? "New" : `From ${origin}`}
                  <span style={WL.groupCount}>{group.length}</span>
                </div>
              )}
              {group.map(t => {
                const i = runningIndex++;
                return (
                  <div key={t.id} style={{
                    ...WL.row,
                    borderLeft:`3px solid ${t.priority === "emergency" ? "#EF4444" : t.priority === "priority" ? "#EAB308" : t.is_overdue ? "#EF4444" : "transparent"}`,
                    background: t.is_overdue ? "rgba(239,68,68,0.05)" : undefined,
                  }}>
                    <div style={WL.position}>{i + 1}</div>
                    <div style={WL.info}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={WL.ticketNum}>{t.ticket_number}</div>
                        {t.group_members?.length > 0 ? (
                          <span title={t.group_members.map(m => m.patient_name).join(", ")} style={{ fontSize: "9px", fontWeight: "700", color: "#60A5FA", background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                            👨‍👩‍👧 +{t.group_members.length}
                          </span>
                        ) : !t.group_id && (
                          <input type="checkbox" title="Tick people who came together, then Link" checked={picked.includes(t.id)} onChange={() => togglePick(t.id)} disabled={isAway} style={{ cursor: "pointer" }} />
                        )}
                        {t.is_overdue && (
                          <span title="Waiting longer than this station's Max Wait Time threshold" style={{ fontSize: "9px", fontWeight: "700", color: "#EF4444", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: "5px", padding: "1px 5px" }}>
                            ⏰ Over TAT
                          </span>
                        )}
                        {t.is_appointment && (
                          <span style={{ fontSize: "9px", fontWeight: "700", color: "#A78BFA", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                            📅
                          </span>
                        )}
                        <LanguageBadge code={t.language} size="small" />
                        {t.is_skipped && (
                          <span title={`Skipped ${t.skip_count} time${t.skip_count === 1 ? "" : "s"} — didn't respond when called`} style={{ fontSize: "9px", fontWeight: "700", color: "#EF4444", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                            ⏭ Skipped{t.skip_count > 1 ? ` ×${t.skip_count}` : ""}
                          </span>
                        )}
                        {t.is_on_hold && (
                          <span title="On hold — won't be auto-called until called deliberately" style={{ fontSize: "9px", fontWeight: "700", color: "#F59E0B", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                            ⏸ On Hold
                          </span>
                        )}
                      </div>
                      <div style={WL.name}>{t.patient_name}{t.group_members?.length > 0 ? ` + ${t.group_members.map(m => m.patient_name.split(" ")[0]).join(", ")}` : ""}</div>
                      {t.insurance_type && <div style={WL.meta}>{t.insurance_type}</div>}
                    </div>
                    <div style={WL.right}>
                      {t.priority !== "normal" && <span style={{ ...WL.priorityChip, color: t.priority === "emergency" ? "#EF4444" : "#EAB308" }}>{t.priority === "emergency" ? "🚨" : "⚡"}</span>}
                    </div>
                    <button
                      onClick={() => onEscalate && onEscalate(t.id, t.ticket_number, t.priority === "emergency" ? "normal" : "emergency")}
                      disabled={isAway}
                      title={t.priority === "emergency" ? "Remove emergency priority" : "Mark as emergency"}
                      style={{
                        background: t.priority === "emergency" ? "#EF4444" : "rgba(239,68,68,0.1)",
                        border: "1px solid rgba(239,68,68,0.3)",
                        borderRadius: "6px", color: t.priority === "emergency" ? "#FFFFFF" : "#EF4444",
                        fontSize: "11px", fontWeight: "600",
                        padding: "4px 8px", cursor: isAway ? "not-allowed" : "pointer",
                        fontFamily: "inherit", opacity: isAway ? 0.5 : 1, flexShrink: 0,
                      }}
                    >
                      🚨
                    </button>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <button
                        onClick={() => onMessage && onMessage(t)}
                        title="Conversation"
                        style={{
                          background: "var(--surface-border)", border: "1px solid rgba(37,99,235,0.3)",
                          borderRadius: "6px", color: "#60A5FA", fontSize: "11px", fontWeight: "600",
                          padding: "4px 8px", cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        💬
                      </button>
                      {t.unread_reply_count > 0 && <UnreadDot />}
                    </div>
                    <button
                      onClick={() => onCallSpecific && onCallSpecific(t.id)}
                      disabled={calling || isAway}
                      style={{
                        background: "var(--surface-border)",
                        border: "1px solid rgba(37,99,235,0.3)",
                        borderRadius: "6px", color: "#60A5FA",
                        fontSize: "11px", fontWeight: "600",
                        padding: "4px 10px", cursor: calling || isAway ? "not-allowed" : "pointer",
                        fontFamily: "inherit", opacity: calling || isAway ? 0.5 : 1,
                        whiteSpace: "nowrap", flexShrink: 0,
                      }}
                    >
                      Call ▶
                    </button>
                    {t.accessibility_mode && t.accessibility_mode !== "none" && (
                      <span
                        style={WL.accessChip}
                        title={ACCESS_LABELS[t.accessibility_mode]}
                      >
                        {ACCESS_ICONS[t.accessibility_mode]}
                      </span>
                    )}
                    <div style={WL.waited}>{minutesAgo(t.issued_at)}m</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Transfer Modal ────────────────────────────────────────────────────────────
function TransferModal({ open, onClose, serving, outcomes, onDone }) {
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const suggested = serving?.suggested_next_station || null;
  // Fresh Cash/NON-type tickets arrive with their category's first stop
  // (e.g. Cashier) preselected; staff can still pick something else.
  useEffect(() => {
    if (!open || !suggested) return;
    const match = outcomes.find(o => !o.is_exit && o.next_station === suggested.id);
    if (match) setSelected(match.id);
  }, [open, serving?.id]);
  const handleTransfer = async () => {
    if (!selected || !serving) return;
    const chosen = outcomes.find(o => o.id === selected);
    if (suggested && chosen && chosen.next_station !== suggested.id) {
      const where = chosen.is_exit ? "end the visit" : `send them to ${chosen.next_station__name || chosen.label_en}`;
      if (!window.confirm(`${serving.ticket_number} normally goes to ${suggested.name} first. Do you really want to ${where}?`)) return;
    }
    setLoading(true);
    try {
      await staffQueue.transfer(serving.id, { outcome_id: selected });
      const outcome = outcomes.find(o => o.id === selected);
      toast.success(outcome?.is_exit ? `${serving.ticket_number} — visit complete.` : `${serving.ticket_number} transferred.`);
      setSelected(null); onDone();
    } catch (e) { toast.error(e.response?.data?.error || "Transfer failed."); }
    finally { setLoading(false); }
  };
  const VS = {
    default: { border:"var(--input-border)", bg:"var(--input-bg)" },
    success: { border:"rgba(34,197,94,0.4)",  bg:"rgba(34,197,94,0.08)"  },
    warning: { border:"rgba(234,179,8,0.4)",  bg:"rgba(234,179,8,0.08)"  },
    danger:  { border:"rgba(239,68,68,0.4)",  bg:"rgba(239,68,68,0.08)"  },
  };
  return (
    <Modal open={open} onClose={onClose} title="Select Outcome" width="480px">
      {serving && (
        <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
          <div style={TM.patientBar}><span style={TM.ticketNum}>{serving.ticket_number}</span><span style={TM.patientName}>{serving.patient_name}</span></div>
          {suggested && (
            <div style={{ fontSize:"13px", color:"#60A5FA", background:"rgba(37,99,235,0.1)", border:"1px solid rgba(37,99,235,0.3)", borderRadius:"8px", padding:"8px 12px" }}>
              💡 This ticket normally goes to <strong>{suggested.name}</strong> first — preselected below.
            </div>
          )}
          <p style={{ fontSize:"13px", color:"var(--text-secondary)", margin:0 }}>Select what happens next for this patient:</p>
          <div style={TM.outcomeGrid}>
            {outcomes.map(o => {
              const vs = VS[o.color] || VS.default; const isSel = selected === o.id;
              return (
                <button key={o.id} onClick={() => setSelected(o.id)} style={{ ...TM.outcomeBtn, border:`2px solid ${isSel ? "#2563EB" : vs.border}`, background: isSel ? "rgba(37,99,235,0.2)" : vs.bg }}>
                  <div style={TM.outcomeLabelEn}>{o.label_en}</div>
                  {o.is_exit ? <div style={TM.outcomeMeta}>✓ End of visit</div> : o.next_station__name ? <div style={TM.outcomeMeta}>→ {o.next_station__name}</div> : null}
                </button>
              );
            })}
          </div>
          {outcomes.length === 0 && <div style={{ color:"#EF4444", fontSize:"13px" }}>No outcomes configured. Ask admin to add outcomes.</div>}
          <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end", paddingTop:"8px", borderTop:"1px solid var(--surface-border)" }}>
            <Btn variant="ghost" onClick={onClose} disabled={loading}>Cancel</Btn>
            <Btn onClick={handleTransfer} disabled={!selected || loading}>{loading ? "Processing…" : "Confirm →"}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function minutesAgo(isoString) {
  if (!isoString) return 0;
  return Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
}

// ── Today's Appointments (entry-point stations only) ────────────────────────
// Contextual — the parent only renders this when queue.todays_appointments
// is non-empty, which the backend already scopes to entry-point stations
// with at least one still-SCHEDULED appointment for today (see my_queue in
// apps/queue/views.py), so there's nothing extra to gate here. Lets staff
// see the full day's booked appointments at a glance and call one directly
// if the patient shows up ahead of their slot — "Call" here promotes a
// SCHEDULED ticket straight to serving. Left uncalled, each one still
// activates into the normal Waiting list on its own exactly as before
// (see activate_due_appointments) — this panel doesn't change that, it's
// purely an earlier-visibility layer on top of it.
function AppointmentQueuePanel({ appointments, onCall, calling, isAway }) {
  return (
    <div style={AQ.root}>
      <div style={AQ.header}>
        <span style={{ fontSize: "14px" }}>📅</span>
        <span style={AQ.headerText}>Today's Appointments</span>
        <span style={AQ.badge}>{appointments.length}</span>
      </div>
      <div style={AQ.hint}>
        Booked for today, not yet in the live queue. Call directly if the patient has already arrived.
      </div>
      {appointments.map(a => (
        <div key={a.id} style={AQ.row}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
              <span style={{ fontSize: "15px", fontWeight: "800", color: "#A78BFA", fontVariantNumeric: "tabular-nums" }}>
                {a.ticket_number}
              </span>
              {a.priority !== "normal" && (
                <span style={{ fontSize: "11px", color: a.priority === "emergency" ? "#EF4444" : "#EAB308" }}>
                  {a.priority === "emergency" ? "🚨" : "⚡"}
                </span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-primary)", fontWeight: "500" }}>{a.patient_name}</div>
            {a.patient_phone && <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>{a.patient_phone}</div>}
          </div>
          <button
            onClick={() => onCall(a.id)}
            disabled={calling || isAway}
            style={{
              background: "rgba(167,139,250,0.15)",
              border: "1px solid rgba(167,139,250,0.3)",
              borderRadius: "8px", color: "#A78BFA",
              fontSize: "11px", fontWeight: "700",
              padding: "6px 12px", cursor: calling || isAway ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: calling || isAway ? 0.5 : 1,
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Call ▶
          </button>
        </div>
      ))}
    </div>
  );
}

const AQ = {
  root:       { background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: "12px", overflow: "hidden", marginTop: "12px" },
  header:     { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(167,139,250,0.1)", borderBottom: "1px solid rgba(167,139,250,0.15)" },
  headerText: { fontSize: "11px", fontWeight: "800", color: "#A78BFA", letterSpacing: "0.08em", textTransform: "uppercase", flex: 1 },
  badge:      { background: "#A78BFA", color: "#fff", fontSize: "11px", fontWeight: "700", borderRadius: "10px", padding: "1px 7px", minWidth: "20px", textAlign: "center" },
  hint:       { fontSize: "11px", color: "var(--text-secondary)", padding: "8px 14px", background: "rgba(167,139,250,0.06)", borderBottom: "1px solid rgba(167,139,250,0.1)", lineHeight: "1.5" },
  row:        { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: "1px solid rgba(167,139,250,0.08)" },
};

function ReturningPanel({ returning, onCall, calling, isAway }) {
  return (
    <div style={RP.root}>
      <div style={RP.header}>
        <span style={{ fontSize: "14px" }}>↩</span>
        <span style={RP.headerText}>Returning Patients</span>
        <span style={RP.badge}>{returning.length}</span>
      </div>
      <div style={RP.hint}>
        These patients are returning from another station. Call them directly without waiting through the normal queue.
      </div>
      {returning.map((r, i) => (
        <div key={r.id} style={RP.row}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
              <span style={{ fontSize: "15px", fontWeight: "800", color: "#F97316", fontVariantNumeric: "tabular-nums" }}>
                {r.ticket_number}
              </span>
              {r.priority !== "normal" && (
                <span style={{ fontSize: "11px", color: r.priority === "emergency" ? "#EF4444" : "#EAB308" }}>
                  {r.priority === "emergency" ? "🚨" : "⚡"}
                </span>
              )}
              {r.is_appointment && (
                <span style={{ fontSize: "9px", fontWeight: "700", color: "#A78BFA", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                  📅 Appointment
                </span>
              )}
              {r.is_on_hold && (
                <span title="On hold — this is why Call Next skips it; use Call here to serve them now" style={{ fontSize: "9px", fontWeight: "700", color: "#F59E0B", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                  ⏸ On Hold
                </span>
              )}
              {r.is_skipped && (
                <span title={`Skipped ${r.skip_count} time${r.skip_count === 1 ? "" : "s"} — didn't respond when called`} style={{ fontSize: "9px", fontWeight: "700", color: "#EF4444", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "5px", padding: "1px 5px" }}>
                  ⏭ Skipped{r.skip_count > 1 ? ` ×${r.skip_count}` : ""}
                </span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-primary)", fontWeight: "500" }}>{r.patient_name}</div>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>
              From: <span style={{ color: "var(--text-secondary)" }}>{r.from_station}</span>
            </div>
          </div>
          <button
            onClick={() => onCall(r.id)}
            disabled={calling || isAway}
            style={{
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.3)",
              borderRadius: "8px", color: "#F97316",
              fontSize: "11px", fontWeight: "700",
              padding: "6px 12px", cursor: calling || isAway ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: calling || isAway ? 0.5 : 1,
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Call ▶
          </button>
        </div>
      ))}
    </div>
  );
}

const RP = {
  root:       { background: "rgba(249,115,22,0.05)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: "12px", overflow: "hidden", marginTop: "12px" },
  header:     { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(249,115,22,0.1)", borderBottom: "1px solid rgba(249,115,22,0.15)" },
  headerText: { fontSize: "11px", fontWeight: "800", color: "#F97316", letterSpacing: "0.08em", textTransform: "uppercase", flex: 1 },
  badge:      { background: "#F97316", color: "#fff", fontSize: "11px", fontWeight: "700", borderRadius: "10px", padding: "1px 7px", minWidth: "20px", textAlign: "center" },
  hint:       { fontSize: "11px", color: "#92400E", padding: "8px 14px", background: "rgba(249,115,22,0.06)", borderBottom: "1px solid rgba(249,115,22,0.1)", lineHeight: "1.5" },
  row:        { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: "1px solid rgba(249,115,22,0.08)" },
};

// ── Sent by You — Still Waiting ──────────────────────────────────────────────
// The quick, no-admin-needed alternative to the facility-admin queue
// override: tickets this staff member personally transferred elsewhere,
// that are still sitting unclaimed (uncalled) at their destination — e.g.
// the receiving station's staff isn't available. Recall pulls it back;
// once someone calls it, it drops off this list (the backend re-checks the
// same "still waiting" condition, so a stale click just 404s harmlessly).
function MyTransfersPanel({ transfers, onRecall, recalling }) {
  return (
    <div style={MT.root}>
      <div style={MT.header}>
        <span style={{ fontSize: "14px" }}>↺</span>
        <span style={MT.headerText}>Sent by You — Still Waiting</span>
        <span style={MT.badge}>{transfers.length}</span>
      </div>
      <div style={MT.hint}>
        Tickets you transferred that no one's picked up yet. If the station you sent it to isn't available, recall it back.
      </div>
      {transfers.map(t => (
        <div key={t.id} style={MT.row}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
              <span style={{ fontSize: "15px", fontWeight: "800", color: "#A78BFA", fontVariantNumeric: "tabular-nums" }}>
                {t.ticket_number}
              </span>
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-primary)", fontWeight: "500" }}>{t.patient_name}</div>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>
              At: <span style={{ color: "var(--text-secondary)" }}>{t.current_station_name}</span>
            </div>
          </div>
          <button
            onClick={() => onRecall(t)}
            disabled={recalling}
            style={{
              background: "rgba(167,139,250,0.15)",
              border: "1px solid rgba(167,139,250,0.3)",
              borderRadius: "8px", color: "#A78BFA",
              fontSize: "11px", fontWeight: "700",
              padding: "6px 12px", cursor: recalling ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: recalling ? 0.5 : 1,
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Recall ↺
          </button>
        </div>
      ))}
    </div>
  );
}

const MT = {
  root:       { background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: "12px", overflow: "hidden", marginTop: "12px" },
  header:     { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(167,139,250,0.1)", borderBottom: "1px solid rgba(167,139,250,0.15)" },
  headerText: { fontSize: "11px", fontWeight: "800", color: "#A78BFA", letterSpacing: "0.08em", textTransform: "uppercase", flex: 1 },
  badge:      { background: "#A78BFA", color: "#fff", fontSize: "11px", fontWeight: "700", borderRadius: "10px", padding: "1px 7px", minWidth: "20px", textAlign: "center" },
  hint:       { fontSize: "11px", color: "var(--text-secondary)", padding: "8px 14px", background: "rgba(167,139,250,0.06)", borderBottom: "1px solid rgba(167,139,250,0.1)", lineHeight: "1.5" },
  row:        { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: "1px solid rgba(167,139,250,0.08)" },
};

// Only rendered when the facility's recall_mode is "redirect" — a simple
// click on "Recall" handles the "simple" mode directly with no modal.
function RecallPickerModal({ ticket, stations, onClose, onConfirm, recalling }) {
  const [target, setTarget] = useState("");
  useEffect(() => { if (ticket) setTarget(""); }, [ticket]);
  const otherStations = stations.filter(s => s.name !== ticket?.current_station_name);

  return (
    <Modal open={!!ticket} onClose={onClose} title={`Recall ${ticket?.ticket_number || ""}`} width="420px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
          Pick a station for <strong style={{ color: "var(--text-primary)" }}>{ticket?.ticket_number}</strong> to rejoin the queue at, instead of where you sent it.
        </div>
        {otherStations.length === 0 ? (
          <div style={{ fontSize: "13px", color: "#EF4444", padding: "10px 0" }}>
            No other stations found.
          </div>
        ) : (
          <select style={selectStyle} value={target} onChange={e => setTarget(e.target.value)}>
            <option value="">— Select a station —</option>
            {otherStations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)" }}>
          <Btn variant="ghost" onClick={onClose} disabled={recalling}>Cancel</Btn>
          <Btn onClick={() => onConfirm(target)} disabled={recalling || !target}>{recalling ? "Recalling…" : "Recall Ticket"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root:         { padding:"24px 28px", fontFamily:"'DM Sans','Segoe UI',sans-serif", minHeight:"100vh", background:"var(--page-bg)" },
  noStation:    { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"70vh", gap:"0" },
  header:       { display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:"24px", flexWrap:"wrap", gap:"12px", paddingBottom:"16px" },
  title:        { fontSize:"24px", fontWeight:"700", color:"var(--text-primary)", margin:"0 0 4px", letterSpacing:"-0.4px" },
  sub:          { fontSize:"14px", color:"var(--text-secondary)", margin:0, display:"flex", alignItems:"center", gap:"8px" },
  stationCode:  { background:"var(--surface-border)", color:"#60A5FA", borderRadius:"5px", padding:"2px 7px", fontSize:"11px", fontWeight:"700", fontFamily:"monospace" },
  headerActions:{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" },
  waitingBadge: { display:"flex", flexDirection:"column", alignItems:"center", background:"var(--table-header-bg)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:"10px", padding:"8px 16px", minWidth:"64px" },
  waitingNum:   { fontSize:"22px", fontWeight:"700", color:"#60A5FA", lineHeight:1 },
  waitingLabel: { fontSize:"10px", color:"var(--text-muted)", fontWeight:"600", letterSpacing:"0.06em", textTransform:"uppercase", marginTop:"2px" },
  layout:       { display:"grid", gridTemplateColumns:"1fr 320px", gap:"20px", alignItems:"start" },
  leftCol:{}, rightCol:{},
};
const SP = {
  root:         { background:"var(--table-header-bg)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:"18px", padding:"28px 24px", display:"flex", flexDirection:"column", gap:"16px", minHeight:"400px" },
  inlineConvo:  { background:"var(--input-bg)", border:"1px solid rgba(37,99,235,0.15)", borderRadius:"14px", padding:"18px" },
  label:        { fontSize:"10px", fontWeight:"700", color:"#2563EB", letterSpacing:"0.12em", textTransform:"uppercase" },
  ticketNum:    { fontSize:"64px", fontWeight:"800", color:"var(--text-primary)", letterSpacing:"-2px", lineHeight:1, fontVariantNumeric:"tabular-nums" },
  priorityBadge:{ display:"inline-flex", alignItems:"center", gap:"6px", borderRadius:"8px", padding:"5px 12px", fontSize:"12px", fontWeight:"700", letterSpacing:"0.06em" },
  patientCard:  { background:"var(--input-bg)", borderRadius:"12px", padding:"14px 16px", display:"flex", flexDirection:"column", gap:"6px" },
  patientName:  { fontSize:"18px", fontWeight:"700", color:"var(--text-primary)" },
  patientDetail:{ fontSize:"13px", color:"var(--text-secondary)" },
  journey:      { display:"flex", flexDirection:"column", gap:"4px", borderLeft:"2px solid rgba(37,99,235,0.2)", paddingLeft:"12px" },
  journeyStep:  { display:"flex", alignItems:"center", gap:"8px" },
  journeyDot:   { width:"8px", height:"8px", borderRadius:"50%", flexShrink:0 },
  journeyStation:{ fontSize:"12px", color:"var(--text-secondary)", fontWeight:"500" },
  journeyOutcome:{ fontSize:"11px", color:"var(--text-muted)" },
  actions:      { display:"flex", gap:"8px", marginTop:"auto" },
  idle:         { flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 0" },
  idleIcon:     { fontSize:"44px", marginBottom:"12px" },
  idleText:     { fontSize:"22px", fontWeight:"700", color:"var(--text-primary)", marginBottom:"6px" },
  idleSub:      { fontSize:"14px", color:"var(--text-secondary)" },
};
const WL = {
  root:         { background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"16px", overflow:"hidden" },
  header:       { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", borderBottom:"1px solid var(--surface-border)" },
  title:        { fontSize:"12px", fontWeight:"700", color:"var(--text-muted)", letterSpacing:"0.08em", textTransform:"uppercase" },
  count:        { background:"var(--surface-border)", color:"#60A5FA", borderRadius:"12px", padding:"2px 10px", fontSize:"13px", fontWeight:"700" },
  empty:        { padding:"32px", textAlign:"center", color:"var(--text-faint)", fontSize:"13px" },
  list:         { maxHeight:"calc(100vh - 220px)", overflowY:"auto" },
  groupHeader:  { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 18px", fontSize:"10px", fontWeight:"700", letterSpacing:"0.06em", textTransform:"uppercase" },
  groupCount:   { fontSize:"10px", fontWeight:"700" },
  row:          { display:"flex", alignItems:"center", gap:"10px", padding:"11px 18px", borderBottom:"1px solid rgba(37,99,235,0.05)", paddingLeft:"15px" },
  position:     { width:"22px", height:"22px", borderRadius:"6px", background:"var(--input-border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", fontWeight:"700", color:"var(--text-secondary)", flexShrink:0 },
  info:         { flex:1, minWidth:0 },
  ticketNum:    { fontSize:"13px", fontWeight:"700", color:"var(--text-primary)" },
  name:         { fontSize:"12px", color:"var(--text-secondary)", marginTop:"1px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  meta:         { fontSize:"10px", color:"var(--text-faint)" },
  right:        { display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"3px", flexShrink:0 },
  priorityChip: { fontSize:"14px" },
  accessChip:   { fontSize:"14px" },
  waited:       { fontSize:"11px", color:"var(--text-faint)", fontVariantNumeric:"tabular-nums" },
};
const TM = {
  patientBar:   { display:"flex", alignItems:"center", gap:"12px", background:"rgba(37,99,235,0.08)", border:"1px solid var(--surface-border)", borderRadius:"10px", padding:"10px 14px" },
  ticketNum:    { fontSize:"20px", fontWeight:"800", color:"#60A5FA", letterSpacing:"-0.5px" },
  patientName:  { fontSize:"15px", fontWeight:"600", color:"var(--text-primary)" },
  outcomeGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" },
  outcomeBtn:   { padding:"12px 14px", borderRadius:"10px", cursor:"pointer", textAlign:"left", transition:"border-color 0.15s, background 0.15s", fontFamily:"inherit" },
  outcomeLabelEn:{ fontSize:"14px", fontWeight:"600", color:"var(--text-primary)", marginBottom:"4px" },
  outcomeMeta:  { fontSize:"11px", color:"var(--text-secondary)" },
};
